// Signal-based i18n. `lang` is reactive — any component that reads
// `t(key)` inside a render re-runs when `lang.value` changes.
//
// Language resolution order on boot:
//   1. ?lang=<code> in the URL hash (embed-time override)
//   2. localStorage "fuel-prices-web:lang" (last user pick)
//   3. navigator.language[:2] if supported
//   4. fallback to "pl"
//
// setLang() persists the choice to both localStorage and the URL hash
// so reloads/share-links keep the language stable.

import { computed, signal } from "@preact/signals";

import type { Lang } from "../types";
import de from "./de";
import en from "./en";
import fr from "./fr";
import it from "./it";
import pl from "./pl";

const dicts = { pl, en, de, fr, it } as const;
type Dict = typeof pl;
export type TranslationKey = keyof Dict;

export const lang = signal<Lang>(detectLang());
const dict = computed<Dict>(() => dicts[lang.value]);

export function t(key: TranslationKey): string {
  return dict.value[key];
}

export function setLang(next: Lang): void {
  lang.value = next;
  try {
    localStorage.setItem("fuel-prices-web:lang", next);
  } catch {
    /* localStorage may be blocked in iframe; ignore */
  }
  const params = new URLSearchParams(location.hash.replace(/^#/, ""));
  params.set("lang", next);
  history.replaceState(null, "", `#${params.toString()}`);
}

function detectLang(): Lang {
  const params = new URLSearchParams(location.hash.replace(/^#/, ""));
  const fromHash = params.get("lang");
  if (fromHash && fromHash in dicts) return fromHash as Lang;

  try {
    const stored = localStorage.getItem("fuel-prices-web:lang");
    if (stored && stored in dicts) return stored as Lang;
  } catch {
    /* ignore */
  }

  const browser = navigator.language.slice(0, 2);
  if (browser in dicts) return browser as Lang;

  return "pl";
}

export const SUPPORTED_LANGS: readonly Lang[] = Object.keys(dicts) as Lang[];
